import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Pagination,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { GlobalAxios } from "../../../../axios/axios-config";
import {
  IPaginatedResponse,
  ISyncPerson,
  ISyncPersonTransactionDetails,
  ISyncTransactionActLog,
} from "../../../../models/sync-person";

const SYNC_PERSON_TRANSACTIONS_ROUTE =
  process.env.REACT_APP_EXTERNAL + "/api/v1/SyncPerson/SyncPersonTransactions";

const translationPath = "page.4uBus.logs.syncPersons.";

interface IProps {
  person: ISyncPerson;
  onClose: () => void;
}

const SyncPersonTransactionsModal: React.FC<IProps> = ({ person, onClose }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] =
    useState<IPaginatedResponse<ISyncPersonTransactionDetails> | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const formatDate = (value?: string | null) => {
    if (!value) return "-";

    return format(new Date(value), "Pp", {
      locale:
        i18n.language === "pt-BR"
          ? ptBR
          : i18n.language === "en-US"
            ? enUS
            : es,
    }).replaceAll(",", "");
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);

      try {
        const response = await GlobalAxios.get<
          IPaginatedResponse<ISyncPersonTransactionDetails>
        >(SYNC_PERSON_TRANSACTIONS_ROUTE, {
          params: { Cpf: person.cpf, PageNumber: pageNumber, PageSize: 10 },
        });

        setData(response.data);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [pageNumber, person.cpf]);

  const renderValue = (label: string, value?: string | number | null) => (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ lineHeight: 1.4, wordBreak: "break-word" }}
      >
        {value ?? "-"}
      </Typography>
    </Box>
  );

  const renderLog = (log: ISyncTransactionActLog, index: number) => (
    <Paper key={log.identifier ?? index} variant="outlined" sx={{ p: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
          alignItems: "end",
          mb: 2,
        }}
      >
        {renderValue(t(translationPath + "identifier"), log.identifier)}
        <Box>
          <Chip
            size="small"
            label={
              log.success === 1
                ? t(translationPath + "success")
                : t(translationPath + "error")
            }
            color={log.success === 1 ? "success" : "error"}
          />
        </Box>
        {renderValue(t(translationPath + "created"), formatDate(log.created))}
        {renderValue(
          t(translationPath + "last_modified"),
          formatDate(log.lastModified),
        )}
      </Box>
      <Typography
        component="pre"
        sx={{
          boxSizing: "border-box",
          m: 0,
          p: 1.5,
          borderRadius: 1,
          backgroundColor: (theme) => theme.palette.grey[100],
          fontFamily: "monospace",
          fontSize: "0.8rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          width: "100%",
          maxWidth: "100%",
          maxHeight: 240,
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        {log.logData || "-"}
      </Typography>
    </Paper>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogActions>
        <Button onClick={onClose}>X</Button>
      </DialogActions>
      <DialogTitle sx={{ paddingY: 0, paddingX: 6, overflow: "hidden" }}>
        {t(translationPath + "transactions")} - {person.name}
      </DialogTitle>
      <DialogContent
        sx={{ height: 500, width: "100%", marginTop: 0, overflow: "auto" }}
      >
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
              columnGap: 4,
              rowGap: 2,
            }}
          >
            {renderValue(t(translationPath + "name"), person.name)}
            {renderValue(t(translationPath + "cpf"), person.cpf)}
            {renderValue(t(translationPath + "email"), person.email)}
            {renderValue(t(translationPath + "system"), person.system)}
            {renderValue(
              t(translationPath + "last_modified"),
              formatDate(person.lastModified),
            )}
          </Box>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : !data?.items.length ? (
          <Typography color="text.secondary">
            {t(translationPath + "no_transactions")}
          </Typography>
        ) : (
          <Stack gap={1}>
            {data.items.map(({ transaction, actions }) => (
              <Accordion key={transaction.identifier}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    columnGap={3}
                    rowGap={1}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ width: "100%", pr: 1 }}
                  >
                    <Typography fontWeight={600}>
                      #{transaction.identifier}
                    </Typography>
                    <Typography>{transaction.originSoftwareName}</Typography>
                    <Chip size="small" label={transaction.statusCode || "-"} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(transaction.created)}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(3, minmax(0, 1fr))",
                      },
                      columnGap: 4,
                      rowGap: 2.5,
                      mb: 2,
                    }}
                  >
                    {renderValue(
                      t(translationPath + "document"),
                      transaction.document,
                    )}
                    {renderValue(
                      t(translationPath + "batch_id"),
                      transaction.batchId,
                    )}
                    {renderValue(
                      t(translationPath + "batch_queue_id"),
                      transaction.batchQueueId,
                    )}
                    {renderValue(
                      t(translationPath + "status"),
                      transaction.statusCode,
                    )}
                    {renderValue(
                      t(translationPath + "message"),
                      transaction.statusMessage,
                    )}
                    {renderValue(
                      t(translationPath + "last_modified"),
                      formatDate(transaction.lastModified),
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="h6" mb={1}>
                    {t(translationPath + "actions")}
                  </Typography>
                  {!actions.length ? (
                    <Typography color="text.secondary">
                      {t(translationPath + "no_actions")}
                    </Typography>
                  ) : (
                    actions.map(({ action, logs }, actionIndex) => (
                      <Accordion key={action.identifier ?? actionIndex}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Stack
                            direction="row"
                            columnGap={3}
                            rowGap={1}
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{ width: "100%", pr: 1 }}
                          >
                            <Typography fontWeight={600}>
                              #{action.identifier ?? "-"}
                            </Typography>
                            <Typography>
                              {action.softwareName || "-"} /{" "}
                              {action.fieldName || "-"}
                            </Typography>
                            <Chip
                              size="small"
                              label={action.statusCode || "-"}
                            />
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                md: "repeat(3, minmax(0, 1fr))",
                              },
                              columnGap: 4,
                              rowGap: 2.5,
                              mb: 2,
                            }}
                          >
                            {renderValue(
                              t(translationPath + "original_value"),
                              action.originalValue,
                            )}
                            {renderValue(
                              t(translationPath + "new_value"),
                              action.newValue,
                            )}
                            {renderValue(
                              t(translationPath + "message"),
                              action.statusMessage,
                            )}
                            {renderValue(
                              t(translationPath + "retries"),
                              action.retries,
                            )}
                            {renderValue(
                              t(translationPath + "created"),
                              formatDate(action.created),
                            )}
                            {renderValue(
                              t(translationPath + "last_modified"),
                              formatDate(action.lastModified),
                            )}
                          </Box>
                          <Typography variant="subtitle1" mb={1}>
                            {t(translationPath + "logs")}
                          </Typography>
                          <Stack gap={1}>
                            {logs.length ? (
                              logs.map(renderLog)
                            ) : (
                              <Typography color="text.secondary">
                                {t(translationPath + "no_logs")}
                              </Typography>
                            )}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    ))
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </DialogContent>
      {!!data?.totalPages && data.totalPages > 1 && (
        <DialogActions sx={{ justifyContent: "center" }}>
          <Pagination
            count={data.totalPages}
            page={pageNumber}
            onChange={(_, page) => setPageNumber(page)}
            disabled={loading}
          />
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SyncPersonTransactionsModal;
